<script setup lang="ts">
import {computed, onUnmounted, ref, watch} from "vue";
import {Component} from "../types.ts";
import {setHideFlag, updateParent} from "../utils/ElementUtils.ts";
import useStore from "../store.ts";
import {RenderInvalidationReason, renderInvalidationRegistry} from "../utils/RenderInvalidation.ts";
import {buildAssetUrl} from "../utils/AssetUrl.ts";
// Original artwork, no third-party license.
import placeholderUrl from "../assets/image-not-found.svg";

const props = defineProps<{
  item: Component
}>();

/* see docs/EXTRACTED_COMMENTS.md#c730 */
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000];
const retryToken = ref(0);
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let attempt = 0;
// Shown instead of a missing image unless hide_unexisting is set.
const showPlaceholder = ref(false);

const visible = computed(() => !props.item.published?.hide_flags?.image);

const cancelRetry = () => {
  if (retryTimer === undefined) return;
  clearTimeout(retryTimer);
  retryTimer = undefined;
};

const scheduleRetry = () => {
  cancelRetry();
  if (attempt >= RETRY_DELAYS_MS.length) return;
  const delay = RETRY_DELAYS_MS[attempt++];
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    retryToken.value++;
  }, delay);
};

const onLoad = () => {
  cancelRetry();
  attempt = 0;
  showPlaceholder.value = false;
  // see docs/EXTRACTED_COMMENTS.md#c731
  if (!props.item.hide_flags?.image) return;
  setHideFlag(props.item.props.id, "image", false, "image_onLoad");
  updateParent(props.item.props.id);
  renderInvalidationRegistry.requestRender(
      RenderInvalidationReason.VisibilityChange,
      [props.item.props.id]
  );
}

const onError = () => {
  // A proto3 bool: the backend sends false as null, so only an explicit true hides.
  if (props.item.image_props?.hide_unexisting) {
    setHideFlag(props.item.props.id, "image", true, "image_onError");
    // Reset render flag to allow unbiased re-evaluation by Ithe renderer
    setHideFlag(props.item.props.id, "render", false, "image_onError");
    updateParent(props.item.props.id);
    renderInvalidationRegistry.requestRender(
        RenderInvalidationReason.VisibilityChange,
        [props.item.props.id]
    );
  } else {
    showPlaceholder.value = true;
  }
  // see docs/EXTRACTED_COMMENTS.md#c730
  scheduleRetry();
}

const width = computed(() => props.item.props.style!.width);
const height = computed(() => props.item.props.style!.height);
const store = useStore();
// Reactive: the content base only exists once the authenticate reply lands.
const environment = computed(() => store.device_settings?.content_url ?? "");

const backgroundSize = computed(() => {
  if (props.item.props.style?.backgroundSize) {
    return props.item.props.style.backgroundSize;
  }
  if (props.item.props.style?.["background-size"]) {
    return props.item.props.style["background-size"];
  }

  switch (props.item.viewType) {
    case "VIEW_NORMAL": {
      return "auto";
    }
    case "VIEW_STRETCH": {
      return "100% 100%";
    }
    default: {
      return "contain";
    }
  }
});

const isCoverMode = computed(() => backgroundSize.value === "cover");
const imageSrc = computed(() => buildAssetUrl(props.item.published?.src, environment.value));
// The probe <img> keeps loading imageSrc (and drives the retry ladder); only what is displayed swaps.
const displaySrc = computed(() => showPlaceholder.value ? placeholderUrl : imageSrc.value);
// Quoted: the inlined SVG data URL contains ' and ( which an unquoted url() rejects.
const backgroundImage = computed(() => `url("${displaySrc.value}")`);

// A new src is a fresh chance: drop the pending ladder.
watch(imageSrc, () => {
  cancelRetry();
  attempt = 0;
  showPlaceholder.value = false;
});

onUnmounted(cancelRetry);
</script>

<template>
  <div :id="item.props.id" v-show="visible">
    <img
        v-if="isCoverMode"
        :key="`cover-${retryToken}`"
        :class="`img-${item.props.id}`"
        :src="displaySrc"
        crossorigin="anonymous"
        :alt="`alt-${item.props.id}`"
        :style="{
        display: 'block',
        width: width,
        height: height,
        objectFit: 'cover',
        objectPosition: item.props.style['align-items'] ?? 'center',
        background: item.props.style?.background,
      }"
    />

    <div v-else :key="`bg-${retryToken}`" :class="`img-${item.props.id}`" :style="{
               display: 'block',
               background: item.props.style!.background,
               backgroundImage: backgroundImage,
               backgroundSize: backgroundSize,
               backgroundPosition: item.props.style['align-items'],
               backgroundRepeat: 'no-repeat',
               width: width,
               height: height,
             }"></div>

    <img :key="`probe-${retryToken}`" :src="imageSrc" crossorigin="anonymous" height="0" width="0" @error="onError" @load="onLoad" :alt="`alt-${item.props.id}`"/>
    <slot></slot>
  </div>

</template>

<style scoped>
</style>